/* eslint-disable no-param-reassign */
import { type Ref, type ShallowReactive, computed } from 'vue'

import type {
  FinderBudgetCriterion,
  FinderConfig,
  FinderCriterion,
  FinderOption,
  FinderRequirementsCriterion,
} from './useFinder.types'

type FacetCriterion = Exclude<
  FinderCriterion,
  FinderBudgetCriterion | FinderRequirementsCriterion
>

export function findCriterion<T extends FinderCriterion['kind']>(
  config: FinderConfig,
  kind: T,
): Extract<FinderCriterion, { kind: T }> | undefined {
  return config.criteria.find(
    (criterion): criterion is Extract<FinderCriterion, { kind: T }> =>
      criterion.kind === kind,
  )
}

export function useFinder(
  config: FinderConfig,
  filtersInput: ShallowReactive<Record<string, string[]>>,
  priceInput: Ref<[number, number] | undefined>,
) {
  const facetCriteria = computed(() =>
    config.criteria.filter(
      (criterion): criterion is FacetCriterion =>
        criterion.kind !== 'budget' && criterion.kind !== 'requirements',
    ),
  )

  function isOptionActive(
    criterion: FacetCriterion,
    option: FinderOption,
  ): boolean {
    if (option.values.length === 0) return false

    const currentValues = filtersInput[criterion.facetName] ?? []

    return option.values.every((v) => currentValues.includes(v))
  }

  function isOptionSelected(
    criterion: FacetCriterion,
    optionId: string,
  ): boolean {
    const option = criterion.options.find((o) => o.id === optionId)

    return option ? isOptionActive(criterion, option) : false
  }

  function toggleOption(criterion: FacetCriterion, optionId: string): void {
    const option = criterion.options.find((o) => o.id === optionId)
    if (!option) return

    const currentValues = filtersInput[criterion.facetName] ?? []

    if (isOptionActive(criterion, option)) {
      filtersInput[criterion.facetName] = currentValues.filter(
        (v) => !option.values.includes(v),
      )
    } else {
      const merged = [...currentValues, ...option.values]
      filtersInput[criterion.facetName] = [...new Set(merged)]
    }
  }

  // Assigns a fresh array so shallowReactive reactivity fires even when values is empty.
  function setCriterionValues(
    criterion: FacetCriterion,
    values: string[],
  ): void {
    filtersInput[criterion.facetName] = [...values]
  }

  function resetCriterion(criterion: FinderCriterion): void {
    if (criterion.kind === 'budget') {
      priceInput.value = undefined
    } else if (criterion.kind !== 'requirements') {
      filtersInput[criterion.facetName] = []
    }
  }

  function reset(): void {
    for (const criterion of config.criteria) {
      resetCriterion(criterion)
    }
  }

  const isFinderActive = computed(() =>
    config.criteria.some((criterion) => {
      if (criterion.kind === 'budget') {
        return priceInput.value !== undefined
      }

      if (criterion.kind === 'requirements') {
        return false
      }

      return (filtersInput[criterion.facetName]?.length ?? 0) > 0
    }),
  )

  const selectedOptionsCount = computed(() =>
    facetCriteria.value.reduce((count, criterion) => {
      return count + (filtersInput[criterion.facetName]?.length ?? 0)
    }, 0),
  )

  return {
    isFinderActive,
    selectedOptionsCount,
    isOptionSelected,
    toggleOption,
    setCriterionValues,
    resetCriterion,
    reset,
  }
}
